'use client';
import Image from 'next/image';
import { useEffect, useLayoutEffect, useState } from 'react';
import debounce from 'lodash/debounce';

type BannerProps = {
  image: {
    desktop: JsonObject;
    mobile: JsonObject;
    desktopRtl?: JsonObject; // Optional prop for desktop RTL image
    mobileRtl?: JsonObject; // Optional prop for mobile RTL image
  };
};

const ImageSwitcher: React.FC<BannerProps> = ({ image }) => {
  const triggerBannerSwitcher = 1000;
  const [isMobile, setIsMobile] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [finalSrc, setFinalSrc] = useState('');

  const getImageSrc = (isMobileView: boolean) => {
    const mobileSrc = '/static/' + image.mobile.link.split('/static/')[1];
    const desktopSrc = '/static/' + image.desktop.link.split('/static/')[1];

    const isRTL = document?.dir === 'rtl'; // 👈 check document.dir

    if (isRTL) {
      const mobileRTL = image.mobileRtl ? '/static/' + image.mobileRtl.link.split('/static/')[1] : null; // Use mobile RTL if available
      const desktopRTL = image.desktopRtl ? '/static/' + image.desktopRtl.link.split('/static/')[1] : null; // Use desktop RTL if available

      if (isMobileView) {
        if (mobileRTL) {
          setFinalSrc(mobileRTL); // Use mobile RTL if available
        } else {
          setFinalSrc(mobileSrc); // if all else fails fallback to normal image
        }
      } else {
        if (desktopRTL) {
          setFinalSrc(desktopRTL); // Use desktop RTL if available
        } else {
          setFinalSrc(desktopSrc); // Fallback to normal desktop image
        }
      }
    } else {
      // Not RTL, just normal flow
      setFinalSrc(isMobileView ? mobileSrc : desktopSrc);
    }
  };

  useLayoutEffect(() => {
    const updateSize = (): void => {
      if (screenSize === window.innerWidth) {
        return;
      }
      setScreenSize(window.innerWidth);
      const mobileView = window.innerWidth <= triggerBannerSwitcher;
      setIsMobile(mobileView);
      getImageSrc(mobileView);
    };

    updateSize();
    window.addEventListener('resize', debounce(updateSize, 150));
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  if (!finalSrc) {
    return null; // or loading spinner
  }

  const imageProps = isMobile ? image.mobile : image.desktop;

  return (
    <Image
      src={finalSrc}
      width={imageProps.width}
      height={imageProps.height}
      alt={imageProps.alt}
      className={imageProps.customClass}
    />
  );
};

export default ImageSwitcher;
